import { CSSProperties, FC, useState } from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  CollapseProps,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  Input,
  Menu,
  Modal,
  Row,
  Select,
  message,
  theme,
} from "antd";
import { useMutation } from "@tanstack/react-query";

import { PlusOutlined, UpOutlined } from "@ant-design/icons";
import { queryKeys } from "../../../services/react-query/queryKeys";
import DeleteSVG from "../../../assets/common/DeleteSVG";
import queryClient from "../../../services/react-query/queryClient";
import { addCommission, updateCommission } from "../../../network/commission";
import { scheduleType } from "../../../enum/scheduleType";
import dayjs from "dayjs";
import RadioButtonWithSVG from "../../common/ui/RadioButtonWithSVG";
import { FormInstance } from "antd/lib";
import { Commission, CommissionSetting } from "../../../types/commissionType";
import { IntlShape, useIntl } from "react-intl";

interface AddAndEditCommissionProps {
  visible: boolean;
  onClose: () => void;
  team_member_id: number;
  data?: Commission | null;
}

interface CommissionPanelProps {
  commissionType: string;
  panelKey: number;
  form: FormInstance<any>;
  selectedOption: string;
  handleOptionSelect: (option: string, name?: string) => void;
}

const CommissionPanel: FC<CommissionPanelProps> = ({
  commissionType,
  panelKey,
  form,
  handleOptionSelect,
  selectedOption,
}) => {
  const intl = useIntl();

  return (
    <div>
      <Form.List name={`commissions_${panelKey}`}>
        {(fields, { add, remove }) => (
          <>
            <Form.Item className="p-2 rounded w-full text-color-primary">
              {fields.map(({ key, name, ...restField }, index) => (
                <Row key={key} gutter={{ xs: 8, sm: 16, md: 24, lg: 15 }}>
                  <Col className="gutter-row" xl={7} lg={12} xs={24} sm={24}>
                    <Form.Item
                      {...restField}
                      required={false}
                      label={intl.formatMessage({
                        id: "startedFrom",
                        defaultMessage: "بدايةً من",
                      })}
                      name={[name, "from"]}
                      initialValue={
                        form.getFieldValue(`commissions_${panelKey}`)[index - 1]
                          ?.to || 0
                      }
                    >
                      <Input
                        placeholder={
                          form.getFieldValue(`commissions_${panelKey}`)[
                            index - 1
                          ]?.to || 0
                        }
                        type="number"
                        defaultValue={
                          form.getFieldValue(`commissions_${panelKey}`)[
                            index - 1
                          ]?.to || 0
                        }
                        className="w-full my-input"
                        disabled={true}
                      />
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" xl={7} lg={12} xs={24} sm={12}>
                    <Form.Item
                      {...restField}
                      required={false}
                      name={[name, "to"]}
                      dependencies={[`commissions_${panelKey}`]}
                      rules={[
                        // {
                        //   required: true,
                        //   // message: "الحقل مطلوب",
                        // },
                        {
                          validator: (_, value) => {
                            const from = form.getFieldValue(
                              `commissions_${panelKey}`
                            )[index]?.from;
                            if (from && value) {
                              if (Number(from) > Number(value)) {
                                return Promise.reject(
                                  intl.formatMessage({
                                    id: "fieldRequiresTobeGreaterThanBeginning",
                                    defaultMessage:
                                      "يجب ان يكون هذ الحقل أكبر من البداية",
                                  })
                                );
                              } else {
                                return Promise.resolve();
                              }
                            } else {
                              return Promise.resolve();
                            }
                          },
                        },
                      ]}
                      label={intl.formatMessage({
                        id: "evenEnd",
                        defaultMessage: "حتى",
                      })}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "enterValue",
                          defaultMessage: "ادخل القيمة",
                        })}
                        className="w-full my-input"
                      />
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" xl={7} lg={12} xs={24} sm={12}>
                    <Form.Item
                      {...restField}
                      required={false}
                      name={[name, "value"]}
                      label={intl.formatMessage({
                        id: "commissionPercentage",
                        defaultMessage: "نسبة العمولة",
                      })}
                    >
                      <Input
                        type="number"
                        placeholder={
                          selectedOption === "percentage"
                            ? intl.formatMessage({
                                id: "commissionPercentage",
                                defaultMessage: "نسبة العمولة",
                              })
                            : intl.formatMessage({
                                id: "commissionPrice",
                                defaultMessage: "سعر العمولة",
                              })
                        }
                        suffix={
                          <>
                            <RadioButtonWithSVG
                              selectedClassName="bg-[#00086D]  text-[#fff]"
                              unselectedClassName="bg-[#EEF0FF] "
                              className="px-2"
                              option={intl.formatMessage({
                                id: "SARshort",
                                defaultMessage: "رس",
                              })}
                              value="fixed"
                              selectedOption={selectedOption}
                              onSelect={handleOptionSelect}
                              name={`commissions_${panelKey}`}
                            />

                            <RadioButtonWithSVG
                              selectedClassName="bg-[#00086D] text-[#fff]"
                              unselectedClassName="bg-[#EEF0FF]"
                              className="px-2"
                              option="%"
                              value="percentage"
                              selectedOption={selectedOption}
                              onSelect={handleOptionSelect}
                              name={`commissions_${panelKey}`}
                            />
                          </>
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col
                    className="gutter-row hidden"
                    xl={7}
                    lg={12}
                    xs={24}
                    sm={12}
                  >
                    <Form.Item
                      {...restField}
                      required={false}
                      name={[name, "commission_type"]}
                      initialValue={commissionType}
                      label={intl.formatMessage({
                        id: "commissionPercentage",
                        defaultMessage: "نسبة العمولة",
                      })}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "commissionPercentage",
                          defaultMessage: "نسبة العمولة",
                        })}
                        className="w-full my-input"
                      />
                    </Form.Item>
                  </Col>

                  <Col
                    className="gutter-row"
                    xl={2}
                    lg={12}
                    xs={24}
                    sm={12}
                    span={12}
                  >
                    <Form.Item label=" ">
                      <Button
                        onClick={() => remove(index)}
                        // disabled={fields.length === 1}
                        className="bg-transparent shadow-none outline-none"
                        block
                        icon={<DeleteSVG />}
                      ></Button>
                    </Form.Item>
                  </Col>
                </Row>
              ))}

              <Flex justify="flex-end">
                <Form.Item>
                  <Button
                    className="border-none shadow-none outline-none bg-transparent"
                    onClick={() => add()}
                    block
                    icon={
                      <PlusOutlined className="bg-[#00086D] text-white p-1 rounded-md p-0" />
                    }
                  >
                    {intl.formatMessage({
                      id: "addNewRow",
                      defaultMessage: "اضف دورة جديدة",
                    })}
                  </Button>
                </Form.Item>
              </Flex>
            </Form.Item>
          </>
        )}
      </Form.List>
    </div>
  );
};

const getItems: (
  panelStyle: CSSProperties,
  form: FormInstance<any>,
  selectedOption: {
    [key: string]: string;
  },
  handleOptionSelect: (option: string, name?: string) => void,
  intl: IntlShape
) => CollapseProps["items"] = (
  panelStyle,
  form,
  selectedOption,
  handleOptionSelect,
  intl
) => [
  {
    key: "1",
    label: intl.formatMessage({
      id: "servicesCommission",
      defaultMessage: "عمولة الخدمات",
    }),
    children: (
      <CommissionPanel
        form={form}
        handleOptionSelect={handleOptionSelect}
        selectedOption={selectedOption["commissions_1"]}
        commissionType={intl.formatMessage({
          id: "services",
          defaultMessage: " الخدمات",
        })}
        panelKey={1}
      />
    ),
    style: panelStyle,
  },
  {
    key: "2",
    label: intl.formatMessage({
      id: "productsCommission",
      defaultMessage: "عمولة المنتجات",
    }),
    children: (
      <CommissionPanel
        form={form}
        handleOptionSelect={handleOptionSelect}
        selectedOption={selectedOption["commissions_2"]}
        commissionType={intl.formatMessage({
          id: "products",
          defaultMessage: " المنتجات",
        })}
        panelKey={2}
      />
    ),
    style: panelStyle,
  },
  {
    key: "3",
    label: intl.formatMessage({
      id: "membershipsCommission",
      defaultMessage: "عمولة العضويات",
    }),
    children: (
      <CommissionPanel
        form={form}
        handleOptionSelect={handleOptionSelect}
        selectedOption={selectedOption["commissions_3"]}
        commissionType={intl.formatMessage({
          id: "memberships",
          defaultMessage: "العضويات",
        })}
        panelKey={3}
      />
    ),
    style: panelStyle,
  },
  {
    key: "4",
    label: intl.formatMessage({
      id: "giftCardsCommission",
      defaultMessage: "عمولة كروت الهدايا",
    }),
    children: (
      <CommissionPanel
        form={form}
        handleOptionSelect={handleOptionSelect}
        selectedOption={selectedOption["commissions_4"]}
        commissionType={intl.formatMessage({
          id: "giftCards",
          defaultMessage: "كروت الهدايا",
        })}
        panelKey={4}
      />
    ),
    style: panelStyle,
  },
];

const AddAndEditCommission: FC<AddAndEditCommissionProps> = ({
  visible,
  onClose,
  team_member_id,
  data,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [selectedOption, setSelectedOption] = useState(
    data
      ? {
          commissions_1:
            data?.commissions?.find(
              (comission: CommissionSetting) =>
                comission?.commission_type === "الخدمات"
            )?.value_type || "percentage",
          commissions_2:
            data?.commissions?.find(
              (comission: CommissionSetting) =>
                comission?.commission_type === "المنتجات"
            )?.value_type || "percentage",
          commissions_3:
            data?.commissions?.find(
              (comission: CommissionSetting) =>
                comission?.commission_type === "العضويات"
            )?.value_type || "percentage",
          commissions_4:
            data?.commissions?.find(
              (comission: CommissionSetting) =>
                comission?.commission_type === "كروت الهداي"
            )?.value_type || "percentage",
        }
      : {
          commissions_1: "percentage",
          commissions_2: "percentage",
          commissions_3: "percentage",
          commissions_4: "percentage",
        }
  );

  const handleOptionSelect = (option: string, name?: string) => {
    if (!name) return;
    setSelectedOption({
      ...selectedOption,
      [name]: option,
    });
  };
  const { mutate: onAddCommissions, isLoading: isAddingCommission } =
    useMutation((params: any) => addCommission(team_member_id, params), {
      onSuccess: (res) => {
        message.success(intl.formatMessage({ id: "submitSuccessfully" }));
        onClose();
        queryClient.invalidateQueries([queryKeys.listCommission]);
      },
      onError: (error) => {
        message.error(
          intl.formatMessage({
            id: "tryAgain",
            defaultMessage: "حاول مرة اخرى",
          })
        );
      },
    });

  const { mutate: onUpdateCommissions, isLoading } = useMutation(
    (params: any) => updateCommission(team_member_id, data?.id, params),
    {
      onSuccess: (res) => {
        message.success(intl.formatMessage({ id: "submitSuccessfully" }));
        onClose();
        queryClient.invalidateQueries([queryKeys.listCommission]);
      },
      onError: (error) => {
        message.error(
          intl.formatMessage({
            id: "tryAgain",
            defaultMessage: "حاول مرة اخرى",
          })
        );
      },
    }
  );

  const onFinish = async (values: any) => {
    const { effective_date, commission_cycle, ...commissionPanels } = values;

    // Combine commissions from all panels into one array
    const commissions = (Object.values(commissionPanels) as any[])
      .filter((panel) => panel)
      .flatMap((panel: any[], index) =>
        panel.map((item: any, i) => {
          return {
            ...item,
            value_type:
              selectedOption[`commissions_${(index + 1) as 1 | 2 | 3 | 4}`],
          };
        })
      );

    // const requestData = {
    //   ...values,
    //   effective_date: values["effective_date"].format("YYYY-MM-DD"),
    //   commissions,
    // };
    const requestData = {
      commission_cycle: values?.commission_cycle,
      effective_date: values["effective_date"].format("YYYY-MM-DD"),
      commissions: commissions?.filter((item) => {
        if (
          item?.value_type &&
          item?.value &&
          item?.commission_type &&
          +item?.from >= 0 &&
          +item?.to >= 0
        ) {
          return item;
        } else {
          return false;
        }
      }),
    };
    if (data && data?.id) {
      onUpdateCommissions(requestData);
    } else {
      onAddCommissions(requestData);
    }
  };

  const { token } = theme.useToken();

  const panelStyle: React.CSSProperties = {
    marginBottom: 14,
    background: "#EEF0FF",
    borderRadius: token.borderRadiusLG,
    border: "none",
  };

  console.log("data", data);

  const effectiveDate = dayjs(data?.effective_date);

  return (
    <Modal
      centered
      open={visible}
      onCancel={onClose}
      okButtonProps={{ disabled: true }}
      cancelButtonProps={{ disabled: true }}
      footer={[]}
      width={740}
    >
      <div className="my-6 text-color-primary text-2xl font-semibold leading-7 text-center">
        {intl.formatMessage({ id: "commission", defaultMessage: "العمولة" })} #
        {data?.id}
      </div>
      <Form
        name="complex-form"
        onFinish={onFinish}
        layout="vertical"
        className="form-style"
        form={form}
        initialValues={
          data
            ? {
                effective_date: effectiveDate,
                commission_cycle: data?.commission_cycle,
                commissions_1: data?.commissions.filter(
                  (commission: {
                    commission_type: string;
                    value_type: string;
                  }) => {
                    return commission.commission_type === "خدمات";
                  }
                ),
                commissions_2: data?.commissions.filter(
                  (commission: {
                    commission_type: string;
                    value_type: string;
                  }) => {
                    return commission.commission_type === "منتجات";
                  }
                ),
                commissions_3: data?.commissions.filter(
                  (commission: {
                    commission_type: string;
                    value_type: string;
                  }) => {
                    return commission.commission_type === "العضويات";
                  }
                ),
                commissions_4: data?.commissions.filter(
                  (commission: {
                    commission_type: string;
                    value_type: string;
                  }) => {
                    return commission.commission_type === "كروت الهدايا";
                  }
                ),
              }
            : {
                commissions_1: [{}],
                commissions_2: [{}],
                commissions_3: [{}],
                commissions_4: [{}],
              }
        }
      >
        <Row gutter={{ xs: 1, sm: 1, md: 8, lg: 14, xl: 14 }}>
          <Col xl={12} lg={8} xs={24} sm={24}>
            <Form.Item
              name="effective_date"
              label="تاريخ بداية تطبيق العمولة"
              className="w-full"
              required={false}
              rules={[
                {
                  required: true,
                  message: "نهايه الدوره مطلوب",
                },
              ]}
            >
              <DatePicker />
            </Form.Item>
          </Col>
          <Col xl={12} lg={8} xs={24} sm={24}>
            <Form.Item
              name="commission_cycle"
              label="تكرار العمولة"
              className="w-full"
              required={false}
              // rules={[{ required: true, message: "وظيفة العضو مطلوب" }]}
            >
              <Select
                placeholder="تكرار العمولة"
                className="w-full my-input"
                options={scheduleType.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Collapse
          bordered={false}
          defaultActiveKey={["1"]}
          expandIcon={({ isActive }) => (
            <UpOutlined rotate={isActive ? 360 : 0} />
          )}
          style={{ background: token.colorBgContainer }}
          items={getItems(
            panelStyle,
            form,
            selectedOption,
            handleOptionSelect,
            intl
          )}
          expandIconPosition="right"
        />

        <Flex gap="middle" align="center" justify="center">
          <Button
            type="primary"
            block
            htmlType="submit"
            loading={isLoading || isAddingCommission}
          >
            {intl.formatMessage({
              id: "save",
              defaultMessage: "حفظ",
            })}
          </Button>
          <Button
            type="primary"
            block
            className="btn-primary-light"
            loading={isLoading || isAddingCommission}
            onClick={onClose}
          >
            {intl.formatMessage({
              id: "cancel",
              defaultMessage: "إلغاء",
            })}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default AddAndEditCommission;
